<template>
  <div v-if="decks.length > 0">
    <ModalsShare
      data-testid="share-deck-btn"
      :showing="isShareMenuOpen"
      :title="deckToBeShared.title"
      :share-url="deckToBeShared.shareUrl"
      analytics-prefix="CompanyPage"
      @update:showing="isShareMenuOpen = false"
    />

    <ClientOnly>
      <Carousel full-bleed>
        <template #title>
          <slot name="title" />
        </template>

        <CarouselSlide v-for="deck in decks" :key="deck.id" class="deck-slides">
          <div class="carousel-deck bg-white" data-testid="carousel-deck">
            <div class="deck-content cursor-pointer flex flex-col md_flex-row gap-4 w-full" @click="goToDeck(deck)">
              <div class="image w-full md_w-auto flex-shrink-0 md_h-24 lg_h-28 xl_h-32">
                <img v-if="deck.file" class="object-cover" :src="deck.file.url || ''" />
                <div v-else class="bg-gray-300" />
              </div>
              <div class="info w-full">
                <div class="flex flex-row-reverse justify-between">
                  <button
                    class="cta float-right ml-2 appearance-none p-0 bg-transparent"
                    data-testid="carousel-deck-share-btn"
                    @click.stop="shareDeck(deck)"
                  >
                    <ArrowUpTrayIcon class="w-6" />
                  </button>
                  <VTag
                    v-for="tag in deck.type_tags"
                    :key="tag"
                    class="type inline-block mr-3 mb-2"
                    data-testid="carousel-deck-type"
                    :text="tag"
                  />
                </div>
                <div class="flex flex-col">
                  <div class="title flex text-gray-900 text-xs mb-2 md_text-sm pb-8 md_pb-0" data-testid="carousel-deck-title">
                    {{ deck.title }}
                  </div>
                  <div class="flex text-gray-600 mt-auto text-xs mb-2" data-testid="carousel-deck-last-updated">
                    {{ formatDateFull(deck.updated_at) }}
                  </div>
                  <div class="tags flex flex-wrap" data-testid="carousel-deck-tags">
                    <VAssetTag v-for="(tag, index) in deck.tags" :key="index" class="mb-2 mr-3" :tag="tag" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CarouselSlide>
      </Carousel>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import type { PropType, Ref, DeepReadonly } from 'vue'
import { ArrowUpTrayIcon } from '@heroicons/vue/24/outline'
import type { Deck } from '@/entities/deck'
import { formatDateFull } from '@/lib/datetime-format-utils'

defineProps({
  decks: {
    type: Array as PropType<DeepReadonly<Deck[]>>,
    default: () => [],
  },
})

const { goToDeck } = useEndpointNavigation()
const isShareMenuOpen: Ref<boolean> = ref(false)

const deckToBeShared = reactive({
  title: '',
  shareUrl: '',
})

const getDeckUrl = (deckUrlSlug: string | number) => {
  return `${window.location.origin}/decks/${deckUrlSlug}`
}

const shareDeck = (deck: DeepReadonly<Deck>) => {
  deckToBeShared.title = deck.title ?? ''
  deckToBeShared.shareUrl = getDeckUrl(deck.url_slug || deck.id)

  isShareMenuOpen.value = true
}
</script>

<style lang="stylus" scoped>
.carousel-deck
  @apply w-full border border-gray-200 p-4 my-2 text-sm rounded-md
.deck-content .image
  aspect-ratio 16 / 9
  & > *
    @apply w-full h-full rounded-md

@supports not (aspect-ratio: 1 / 1)
  .deck-content .image.h-24
    width 6rem * (16 / 9)

  @screen sm
    .deck-content .image.sm_h-28
      width 7rem * (16 / 9)

  @screen md
    .deck-content .image.md_h-32
      width 8rem * (16 / 9)

  @screen lg
    .deck-content .image.lg_h-36
      width 9rem * (16 / 9)

.deck-slides
  --slides-per-page: 1.03
@screen lg
  .deck-slides
    --slides-per-page: 2
</style>
